import React, { useState, useEffect } from 'react';
import {
    collection,
    addDoc,
    getDocs,
    deleteDoc,
    doc,
    query,
    orderBy
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { db, storage } from '../firebaseConfig';

// Componente de Ícone Simplificado (Reutilizado)
const Icon = ({ name }) => {
    const icons = {
        trash: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        ),
        upload: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        ),
        plus: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v8m0 0v8m0-8h8m-8 0H4" />
        ),
        spinner: (
            <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 0v4" />
        ),
        user: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        )
    };
    const spinClass = name === 'spinner' ? 'animate-spin' : '';
    return (
        <svg className={`w-5 h-5 inline-block ${spinClass}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {icons[name] || null}
        </svg>
    );
};

const F1DriversManager = () => {
    const [drivers, setDrivers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [newDriverName, setNewDriverName] = useState('');
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [newDriverPhoto, setNewDriverPhoto] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        fetchTeams();
        fetchDrivers();
    }, []);

    const fetchTeams = async () => {
        try {
            const q = query(collection(db, "f1_teams"), orderBy("name"));
            const querySnapshot = await getDocs(q);
            const teamsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTeams(teamsData);
        } catch (error) {
            console.error("Erro ao buscar equipes:", error);
        }
    };

    const fetchDrivers = async () => {
        setFetching(true);
        try {
            const q = query(collection(db, "f1_drivers"), orderBy("name"));
            const querySnapshot = await getDocs(q);
            const driversData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setDrivers(driversData);
        } catch (error) {
            console.error("Erro ao buscar pilotos:", error);
        } finally {
            setFetching(false);
        }
    };

    const handleAddDriver = async (e) => {
        e.preventDefault();
        if (!newDriverName || !selectedTeamId) return;
        setLoading(true);

        try {
            let photoUrl = '';
            if (newDriverPhoto) {
                const storageRef = ref(storage, `f1_drivers/${Date.now()}_${newDriverPhoto.name}`);
                await uploadBytes(storageRef, newDriverPhoto);
                photoUrl = await getDownloadURL(storageRef);
            }

            const selectedTeam = teams.find(t => t.id === selectedTeamId);

            await addDoc(collection(db, "f1_drivers"), {
                name: newDriverName,
                teamId: selectedTeamId,
                teamName: selectedTeam ? selectedTeam.name : 'Desconhecida',
                photo: photoUrl
            });

            setNewDriverName('');
            setSelectedTeamId('');
            setNewDriverPhoto(null);
            fetchDrivers();
        } catch (error) {
            console.error("Erro ao adicionar piloto:", error);
            alert("Erro ao adicionar piloto.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDriver = async (driverId, photoUrl) => {
        if (!window.confirm("Tem certeza que deseja excluir este piloto?")) return;

        try {
            await deleteDoc(doc(db, "f1_drivers", driverId));

            if (photoUrl) {
                try {
                    const storageRef = ref(storage, photoUrl);
                    await deleteObject(storageRef);
                } catch (storageError) {
                    console.warn("Erro ao deletar foto (pode já ter sido deletada):", storageError);
                }
            }

            fetchDrivers();
        } catch (error) {
            console.error("Erro ao excluir piloto:", error);
            alert("Erro ao excluir piloto.");
        }
    };

    return (
        <div className="bg-gray-800 p-6 rounded-xl shadow-lg mt-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <span className="text-yellow-500">🏁</span> Gerenciar Pilotos F1
            </h2>

            {/* Form de Adição */}
            <form onSubmit={handleAddDriver} className="bg-gray-700 p-4 rounded-lg mb-8 border border-gray-600">
                <h3 className="text-lg font-semibold text-gray-200 mb-4">Novo Piloto</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div>
                        <label className="block text-gray-400 mb-1 text-sm">Nome do Piloto</label>
                        <input
                            type="text"
                            value={newDriverName}
                            onChange={(e) => setNewDriverName(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded focus:outline-none focus:border-teal-500 text-white"
                            placeholder="Ex: Lewis Hamilton"
                        />
                    </div>
                    <div>
                        <label className="block text-gray-400 mb-1 text-sm">Equipe</label>
                        <select
                            value={selectedTeamId}
                            onChange={(e) => setSelectedTeamId(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded focus:outline-none focus:border-teal-500 text-white"
                        >
                            <option value="">Selecione uma equipe</option>
                            {teams.map(team => (
                                <option key={team.id} value={team.id}>{team.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <label className="block text-gray-400 mb-1 text-sm">Foto (Opcional)</label>
                            <label className="flex items-center justify-center px-4 py-2 bg-gray-600 border border-gray-500 rounded cursor-pointer hover:bg-gray-500 transition-colors w-full">
                                <Icon name="upload" />
                                <span className="ml-2 text-sm text-gray-300 truncate">
                                    {newDriverPhoto ? "Foto Selecionada" : "Foto"}
                                </span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => setNewDriverPhoto(e.target.files[0])}
                                />
                            </label>
                        </div>
                        <button
                            type="submit"
                            disabled={loading || !newDriverName || !selectedTeamId}
                            className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-2 px-6 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {loading ? <Icon name="spinner" /> : <Icon name="plus" />}
                        </button>
                    </div>
                </div>
            </form>

            {/* Lista de Pilotos */}
            <div className="space-y-2">
                {fetching ? (
                    <p className="text-gray-400 text-center py-4">Carregando pilotos...</p>
                ) : drivers.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nenhum piloto cadastrado.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {drivers.map((driver) => (
                            <div key={driver.id} className="bg-gray-700 p-4 rounded-lg flex items-center justify-between border border-gray-600 hover:border-gray-500 transition-colors">
                                <div className="flex items-center gap-3">
                                    {driver.photo ? (
                                        <img src={driver.photo} alt={driver.name} className="w-12 h-12 object-cover rounded-full border-2 border-gray-500" />
                                    ) : (
                                        <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center border border-gray-500">
                                            <Icon name="user" />
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-white font-medium">{driver.name}</p>
                                        <p className="text-xs text-gray-400">{driver.teamName}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleDeleteDriver(driver.id, driver.photo)}
                                    className="text-red-400 hover:text-red-300 p-2 hover:bg-red-400/10 rounded transition-colors"
                                    title="Excluir Piloto"
                                >
                                    <Icon name="trash" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default F1DriversManager;
